import {
  useEffect,
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { TooltipTitle } from './TooltipTitle';

/** A box on a page image, in that image's pixels. */
export type PageBox = { x: number; y: number; width: number; height: number };

/** The smallest box around every word of the evidence lines, or null when none is drawn. */
export function evidenceBox(
  words: readonly { lineIndex: number; box: PageBox; measuredBox?: PageBox | null }[],
  lines: readonly number[],
): PageBox | null {
  // Where the ink is: the worker's measured box when it sent one, else the line's even split.
  const hits = words
    .filter((word) => lines.includes(word.lineIndex))
    .map((word) => ({ box: word.measuredBox ?? word.box }));
  if (hits.length === 0) return null;
  const left = Math.min(...hits.map((word) => word.box.x));
  const top = Math.min(...hits.map((word) => word.box.y));
  const right = Math.max(...hits.map((word) => word.box.x + word.box.width));
  const bottom = Math.max(...hits.map((word) => word.box.y + word.box.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

// The chart's own coordinates: a track from 100 at the top to 50 near the bottom, then Off.
// It is always 177 wide; its height follows the box the card gives it (160 at the smallest),
// so a taller card draws a taller plot while text and dots keep their size.
const WIDTH = 177,
  SMALLEST_HEIGHT = 160,
  TOP = 8,
  PLOT_LEFT = 44;
/** Where each score sits on a chart `height` units tall, and the score at a height. */
function chartRows(height: number) {
  const bottom = height - 22,
    off = height - 6;
  return {
    bottom,
    yOf: (score: number) =>
      score <= 0 ? off : TOP + ((100 - Math.max(50, Math.min(100, score))) / 50) * (bottom - TOP),
    scoreAt: (y: number) =>
      y > bottom + 8
        ? 0
        : Math.round(100 - ((Math.min(bottom, Math.max(TOP, y)) - TOP) / (bottom - TOP)) * 50),
  };
}
/** Most dots drawn; a larger batch is thinned evenly, and the counts beside the chart stay exact. */
const MOST_DOTS = 160;

/**
 * The review threshold as a picture: one dot per recent reading at its confidence, the
 * threshold as a dashed line, and the readings below it (the ones a person would be
 * asked to check) in amber. Drag the handle, or use the slider it stands for.
 */
export function ReviewThresholdChart({
  value,
  histogram,
  disabled,
  label,
  onChange,
}: {
  /** The threshold, 0 to 1; 0 is Off. */
  value: number;
  /** Measured readings per whole-percent score, 101 buckets; absent when none were measured. */
  histogram?: readonly number[];
  disabled: boolean;
  label: string;
  onChange: (value: number) => void;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  // The height, in chart units, that fills the box at the same scale as the width.
  const [height, setHeight] = useState(SMALLEST_HEIGHT);
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box && box.width > 0 && box.height > 0)
        setHeight(Math.max(SMALLEST_HEIGHT, Math.round((WIDTH * box.height) / box.width)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const { bottom, yOf, scoreAt } = chartRows(height);
  const shown = Math.round(value * 100);
  const scores: number[] = [];
  histogram?.forEach((count, score) => {
    for (let index = 0; index < count; index++) scores.push(score);
  });
  scores.sort((left, right) => right - left);
  const step = Math.max(1, scores.length / MOST_DOTS);
  const dots = scores.filter((_, index) => index % step < 1);
  const lineY = yOf(shown);
  const pick = (event: ReactPointerEvent<SVGSVGElement>) => {
    const box = svg.current?.getBoundingClientRect();
    if (!box || box.height === 0) return;
    onChange(scoreAt(((event.clientY - box.top) * height) / box.height) / 100);
  };
  const tick = (score: number, text: string) =>
    Math.abs(yOf(score) - lineY) > 9 ? (
      <text key={text} x={22} y={yOf(score) + 4} className="setup-chart-tick">
        {text}
      </text>
    ) : null;
  return (
    <div ref={frame} className="setup-chart" data-disabled={disabled || undefined}>
      <svg
        ref={svg}
        viewBox={`0 0 ${WIDTH} ${height}`}
        aria-hidden="true"
        onPointerDown={
          disabled
            ? undefined
            : (event) => {
                dragging.current = true;
                event.currentTarget.setPointerCapture(event.pointerId);
                pick(event);
              }
        }
        onPointerMove={
          disabled
            ? undefined
            : (event) => {
                if (dragging.current) pick(event);
              }
        }
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
      >
        {shown > 0 && (
          <rect
            x={PLOT_LEFT}
            y={lineY}
            width={WIDTH - PLOT_LEFT}
            height={Math.max(0, bottom - lineY)}
            rx={6}
            className="setup-chart-zone"
          />
        )}
        <rect x={30} y={TOP} width={6} height={bottom - TOP} rx={3} className="setup-chart-track" />
        {tick(100, '100')}
        {tick(75, '75')}
        {tick(50, '50')}
        {tick(0, 'Off')}
        <text x={22} y={lineY + 4} className="setup-chart-tick is-current">
          {shown > 0 ? shown : 'Off'}
        </text>
        {dots.map((score, index) => (
          <circle
            key={index}
            cx={PLOT_LEFT + 8 + ((index * 47) % (WIDTH - PLOT_LEFT - 16))}
            cy={yOf(Math.max(score, 50)) + ((index % 3) - 1) * 1.6}
            r={score < shown ? 2.8 : 2.3}
            className={score < shown ? 'setup-chart-dot is-review' : 'setup-chart-dot'}
          />
        ))}
        {shown > 0 && <path d={`M${PLOT_LEFT} ${lineY}H${WIDTH}`} className="setup-chart-line" />}
        <circle cx={33} cy={lineY} r={8} className="setup-chart-handle" />
        {shown > 0 && (
          <text x={WIDTH - 6} y={bottom - 7} className="setup-chart-note">
            asks for review
          </text>
        )}
      </svg>
      {!disabled && (
        <input
          type="range"
          className="visually-hidden"
          aria-label={label}
          aria-valuetext={shown > 0 ? `Review below ${shown}` : 'Off'}
          min={0}
          max={100}
          step={1}
          value={shown}
          onChange={(event) => onChange(Number(event.target.value) / 100)}
        />
      )}
    </div>
  );
}

/** A field with its own bar, marked on the job's Field values chart at that bar. */
export type ThresholdMark = { value: number; label: string; title: string };

/** Pixels around the job chart's plot: the track and its numbers sit left of the dots. */
const JOB_CHART = { top: 10, bottom: 10, track: 33, plot: 46, right: 6, dot: 2.4, rowPoints: 2 };

/**
 * A job-wide review threshold as a picture, 0 to 100 (owner decision, 2026-10-03): one dot
 * per document of the latest batch at the confidence this check judges, in rows two points
 * apart; the bar as a dashed line with a handle; the documents this check would hold in amber.
 * Drag the handle, or use the slider it stands for. `held` is the engine's own count of held
 * documents per whole percent; a field with its own bar makes it differ from the line, so
 * when it is given it colours the dots (ringed where it disagrees with the line). Until then
 * the line decides. The chart is drawn in pixels so dots and text keep their size as it grows.
 */
export function JobThresholdChart({
  value,
  histogram,
  held,
  marks = [],
  disabled,
  tone = 'review',
  label,
  zoneLabel,
  onChange,
}: {
  /** The bar, 0 to 1; a document strictly below it is held, and 0 never asks. */
  value: number;
  /** Documents per whole-percent confidence, 101 buckets; absent before anything is read. */
  histogram?: readonly number[] | null;
  held?: readonly number[] | null;
  marks?: readonly ThresholdMark[];
  disabled: boolean;
  /** "note" where Autopilot sends documents out unchecked: nothing below a bar is held, so the caught dots and their zone take the quiet note colour. */
  tone?: 'review' | 'note';
  label: string;
  /** What happens to the amber dots, in the pilot mode's words: "asks for review", "goes out with a note"… */
  zoneLabel: string;
  onChange: (value: number) => void;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  const [{ width, height }, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box) setSize({ width: Math.round(box.width), height: Math.round(box.height) });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const { top, bottom, track, plot, right, dot, rowPoints } = JOB_CHART;
  const span = Math.max(1, height - top - bottom);
  const yOf = (score: number) => top + ((100 - score) / 100) * span;
  const scoreAt = (y: number) =>
    Math.round(100 - ((Math.min(height - bottom, Math.max(top, y)) - top) / span) * 100);
  const bar = Math.round(value * 100);
  const lineY = yOf(bar);
  // Rows of dots, each two points tall, thinned evenly past MOST_DOTS so a big batch still fits;
  // the counts beside the chart stay exact.
  const total = histogram?.reduce((sum, count) => sum + count, 0) ?? 0;
  const scale = total > MOST_DOTS ? MOST_DOTS / total : 1;
  const rows: { y: number; dots: string[] }[] = [];
  if (histogram)
    for (let first = 0; first <= 100; first += rowPoints) {
      const dots: string[] = [];
      for (let score = Math.min(100, first + rowPoints - 1); score >= first; score--) {
        const count = histogram[score] ?? 0;
        if (!count) continue;
        const shown = Math.max(1, Math.round(count * scale));
        const heldHere = held
          ? Math.min(
              shown,
              Math.round((held[score] ?? 0) * scale) || ((held[score] ?? 0) > 0 ? 1 : 0),
            )
          : null;
        for (let index = 0; index < shown; index++) {
          if (disabled) {
            dots.push('job-chart-dot is-off');
            continue;
          }
          const isHeld = heldHere === null ? score < bar : index < heldHere;
          dots.push(
            isHeld
              ? `job-chart-dot is-${tone}${score >= bar ? ' is-own' : ''}`
              : `job-chart-dot${score < bar ? ' is-free' : ''}`,
          );
        }
      }
      if (dots.length)
        rows.push({ y: yOf(first + (Math.min(100, first + rowPoints - 1) - first) / 2), dots });
    }
  const longest = Math.max(1, ...rows.map((row) => row.dots.length));
  const step = Math.min(2 * dot + 1.4, Math.max(1, width - plot - right - 2 * dot) / longest);
  // Own-bar labels step apart when two bars sit close together; each mark stays at its value.
  let lastLabel = -99;
  const placedMarks = [...marks]
    .sort((a, b) => b.value - a.value)
    .map((mark) => {
      const y = yOf(mark.value * 100),
        labelY = Math.max(y, lastLabel + 13);
      lastLabel = labelY;
      return { ...mark, y, labelY };
    });
  const tick = (score: number) =>
    Math.abs(yOf(score) - lineY) > 11 ? (
      <text key={score} x={track - 11} y={yOf(score) + 4} className="job-chart-tick">
        {score}
      </text>
    ) : null;
  const pick = (event: ReactPointerEvent<SVGSVGElement>) => {
    const box = svg.current?.getBoundingClientRect();
    if (!box || box.height === 0) return;
    onChange(scoreAt(((event.clientY - box.top) * height) / box.height) / 100);
  };
  return (
    <div ref={frame} className="job-chart" data-disabled={disabled || undefined}>
      {width > 0 && height > 0 && (
        <svg
          ref={svg}
          viewBox={`0 0 ${width} ${height}`}
          width={width}
          height={height}
          aria-hidden="true"
          onPointerDown={
            disabled
              ? undefined
              : (event) => {
                  dragging.current = true;
                  event.currentTarget.setPointerCapture(event.pointerId);
                  pick(event);
                }
          }
          onPointerMove={
            disabled
              ? undefined
              : (event) => {
                  if (dragging.current) pick(event);
                }
          }
          onPointerUp={() => {
            dragging.current = false;
          }}
          onPointerCancel={() => {
            dragging.current = false;
          }}
        >
          {!disabled && bar > 0 && (
            <rect
              x={plot - 5}
              y={lineY}
              width={Math.max(0, width - plot + 5 - right)}
              height={Math.max(0, height - bottom - lineY + 6)}
              rx={6}
              className={`job-chart-zone is-${tone}`}
            />
          )}
          <rect x={track - 3} y={top} width={6} height={span} rx={3} className="job-chart-track" />
          {tick(100)}
          {tick(75)}
          {tick(50)}
          {tick(25)}
          {tick(0)}
          {rows.map((row) =>
            row.dots.map((className, index) => (
              <circle
                key={`${row.y}:${index}`}
                cx={plot + dot + index * step}
                cy={row.y}
                r={dot}
                className={className}
              />
            )),
          )}
          {!histogram &&
            (() => {
              // Above the line when there is room and no own-bar label sits there; otherwise below it.
              const above = lineY - top > 60 && !placedMarks.some((mark) => mark.labelY < lineY);
              const y = above ? (top + lineY) / 2 : (lineY + height - bottom) / 2;
              return (
                <text x={(plot + width - right) / 2} y={y - 6} className="job-chart-empty">
                  <tspan x={(plot + width - right) / 2}>Dots appear after</tspan>
                  <tspan x={(plot + width - right) / 2} dy={16}>
                    documents are read
                  </tspan>
                </text>
              );
            })()}
          {!disabled &&
            placedMarks.map((mark) => (
              <g key={mark.label} className="job-chart-own">
                <TooltipTitle>{mark.title}</TooltipTitle>
                <path d={`M${width - right - 22} ${mark.y}H${width - right}`} />
                <text x={width - right - 26} y={mark.labelY + 4}>
                  {mark.label}
                </text>
              </g>
            ))}
          <path d={`M${plot - 5} ${lineY}H${width - right}`} className="job-chart-line" />
          <text x={track - 11} y={lineY + 4} className="job-chart-tick is-current">
            {bar}
          </text>
          <circle cx={track} cy={lineY} r={7.5} className="job-chart-handle" />
          {histogram && !disabled && height - bottom - lineY > 24 && (
            <text x={width - right - 4} y={lineY + 16} className={`job-chart-note is-${tone}`}>
              {zoneLabel}
            </text>
          )}
        </svg>
      )}
      {!disabled && (
        <input
          type="range"
          className="visually-hidden"
          aria-label={label}
          aria-valuetext={bar > 0 ? `Review below ${bar}` : 'Never asks'}
          min={0}
          max={100}
          step={1}
          value={bar}
          onChange={(event) => onChange(Number(event.target.value) / 100)}
        />
      )}
    </div>
  );
}

/**
 * Where the value sits on a real page: the page image cropped around it, the value outlined
 * and centred in whatever height the card gives the crop.
 */
export function FieldOnPage({ previewPath, box }: { previewPath: string; box: PageBox }) {
  const frame = useRef<HTMLDivElement>(null);
  const [source, setSource] = useState<string | null>(null);
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [{ width, height }, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    let live = true;
    setSource(null);
    setNatural(null);
    void window.capturePointDesktop
      ?.readPagePreview(previewPath)
      .then((value) => {
        if (live) setSource(value || '');
      })
      .catch(() => {
        if (live) setSource('');
      });
    return () => {
      live = false;
    };
  }, [previewPath]);
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      setSize({ width: rect?.width ?? 0, height: rect?.height ?? 0 });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const scale =
    natural && width > 0 && height > 0
      ? Math.min(1.2, Math.max(width / natural.width, (width * 0.55) / Math.max(1, box.width)))
      : 0;
  const left = width / 2 - (box.x + box.width / 2) * scale;
  const top = height / 2 - (box.y + box.height / 2) * scale;
  return (
    <div ref={frame} className="setup-crop" aria-hidden="true">
      {source && (
        <img
          src={source}
          alt=""
          draggable={false}
          onLoad={(event) =>
            setNatural({
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            })
          }
          style={
            natural && scale
              ? { left, top, width: natural.width * scale }
              : { visibility: 'hidden' }
          }
        />
      )}
      {source && natural && scale > 0 && (
        <span
          className="setup-crop-mark"
          style={{
            left: left + box.x * scale - 4,
            top: top + box.y * scale - 4,
            width: box.width * scale + 8,
            height: box.height * scale + 8,
          }}
        />
      )}
    </div>
  );
}

/**
 * A value that fits its box instead of being cut off. It wraps between words onto at most
 * `lines` lines, stepping down from its CSS font size to `smallest` pixels until it fits; a value
 * that still does not fit then breaks inside words, and its box grows. The whole value is its title.
 */
export function FitText({
  text,
  className,
  lines = 2,
  smallest = 15,
}: {
  text: string;
  className?: string;
  lines?: number;
  smallest?: number;
}) {
  const element = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const node = element.current;
    if (!node) return;
    let width = -1;
    const fits = () =>
      node.scrollWidth <= node.clientWidth + 1 &&
      node.scrollHeight <= parseFloat(getComputedStyle(node).lineHeight) * lines + 1;
    // Refit only when the width changes: a new size changes the height, never the width, so this cannot loop.
    const fit = () => {
      if (node.clientWidth === width) return;
      width = node.clientWidth;
      node.style.fontSize = '';
      node.style.overflowWrap = '';
      for (let size = parseFloat(getComputedStyle(node).fontSize); size >= smallest; size -= 1) {
        node.style.fontSize = `${size}px`;
        if (fits()) return;
      }
      node.style.overflowWrap = 'anywhere';
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(node);
    return () => observer.disconnect();
  }, [text, lines, smallest]);
  return (
    <strong ref={element} className={className} title={text}>
      {text}
    </strong>
  );
}
